/**
 * SignpostingLocationRequestAnnotation
 *
 * Emits signposting:location-received window event.
 * useChatHandler listens and calls handleSendMessage directly.
 */

'use client'

import { useCallback, useContext, useState } from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'
import { SignpostingLocationPrompt } from '@/components/chat/signposting-location-prompt'
import { emitLocationReceived } from '@/lib/signposting/events'
import { ChatbotUIChatContext } from '@/context/chat'

// --------------

interface LocationRequestData {
  reason: string
}

// --------------

export const SignpostingLocationRequestAnnotation: React.FC<{
  annotation: Annotation | Annotation2
}> = ({ annotation }) => {
  const [declined, setDeclined] = useState(false)
  const [sent, setSent] = useState(false)
  const { selectedChat, chatMessages, isGenerating } = useContext(ChatbotUIChatContext)

  const raw = (annotation as Record<string, unknown>)[
    'signposting__requestLocation'
  ] as Record<string, unknown> | undefined
  const data: LocationRequestData | null = raw
    ? (('result' in raw ? raw.result : raw) as LocationRequestData)
    : null

  const locationAlreadySent = chatMessages.some(
    (msg) =>
      msg.message.role === 'user' &&
      msg.message.content.startsWith('My location is:')
  )

  // --------------

  const handleLocationReceived = useCallback(
    (location: { latitude: number; longitude: number }) => {
      if (sent) return
      const chatId = selectedChat?.id || ''
      if (!chatId) return
      setSent(true)
      emitLocationReceived({
        latitude: location.latitude,
        longitude: location.longitude,
        chatId,
        savePreference: false
      })
    },
    [selectedChat?.id, sent]
  )

  // --------------

  if (!raw || !data?.reason) return null
  if (sent || declined || locationAlreadySent) return null
  // Only show after generation is done so the send works
  if (isGenerating) return null

  return (
    <div className="py-2">
      <SignpostingLocationPrompt
        reason={data.reason}
        onLocationReceived={handleLocationReceived}
        onDeclined={() => setDeclined(true)}
      />
    </div>
  )
}
